import React, { useState, useEffect, useRef, useCallback } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';
import SideHelpPanel from './components/SideHelpPanel';
import ProductNav from './components/ProductNav';

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// eslint-disable-next-line no-unused-vars
function formatFamily(slug) {
  if (!slug) return '';
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function isRecentlyUpdated(isoDate) {
  if (!isoDate) return false;
  const d = new Date(isoDate);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  return d > thirtyDaysAgo;
}

function buildHierarchy(results) {
  if (!results) return [];
  const { generators = [], families = [], sections = [] } = results;

  const sectionMap = {};
  sections.forEach(sec => {
    sectionMap[sec.section] = { ...sec, families: {} };
  });

  families.forEach(fam => {
    const secKey = fam.section;
    if (!sectionMap[secKey]) {
      sectionMap[secKey] = { section: secKey, sectionDisplayName: fam.sectionDisplayName, domain: fam.domain, domainDisplayName: fam.domainDisplayName, families: {} };
    }
    sectionMap[secKey].families[fam.family] = { ...fam, generators: [] };
  });

  generators.forEach(gen => {
    const secKey = gen.section;
    const famKey = gen.family;
    if (!sectionMap[secKey]) {
      sectionMap[secKey] = { section: secKey, sectionDisplayName: gen.sectionDisplayName, domain: gen.domain, domainDisplayName: gen.domainDisplayName, families: {} };
    }
    if (!sectionMap[secKey].families[famKey]) {
      sectionMap[secKey].families[famKey] = { family: famKey, familyDisplayName: gen.familyDisplayName, section: secKey, sectionDisplayName: gen.sectionDisplayName, generators: [] };
    }
    sectionMap[secKey].families[famKey].generators.push(gen);
  });

  return Object.values(sectionMap).map(sec => ({
    ...sec,
    families: Object.values(sec.families),
  }));
}

export default function SearchResults() {
  const navigate = useNavigate();
  useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlQ      = searchParams.get('q')      || '';
  const urlDomain = searchParams.get('domain') || 'dnd';
  const urlPage   = parseInt(searchParams.get('page') || '1', 10);
  const urlSort   = searchParams.get('sort')   || 'relevance';

  const [searchInput,  setSearchInput]  = useState(urlQ);
  const [autoResults,  setAutoResults]  = useState({ generators: [], families: [], sections: [] });
  const [showAuto,     setShowAuto]     = useState(false);
  const debouncedInput = useDebounce(searchInput, 280);
  const searchRef = useRef(null);

  const [results,     setResults]     = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [sectionView, setSectionView] = useState('cards');

  const fetchResults = useCallback(async (q, domain, page, sort) => {
    if (!q.trim()) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ q, domain, page, per_page: 25, sort });
      const res = await API.get(`/v1/search?${params}`);
      setResults(res.data);
    } catch {
      setError('Search failed. Please try again.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (urlQ) {
      setSearchInput(urlQ);
      fetchResults(urlQ, urlDomain, urlPage, urlSort);
    }
  }, [urlQ, urlDomain, urlPage, urlSort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAutoResults({ generators: [], families: [], sections: [] });
    setShowAuto(false);
  }, [urlDomain]);

  useEffect(() => {
    if (debouncedInput.length < 2) { setAutoResults({ generators: [], families: [], sections: [] }); setShowAuto(false); return; }
    if (debouncedInput === urlQ) return;
    fetchAutocomplete(debouncedInput);
  }, [debouncedInput]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAutocomplete(q) {
    try {
      const res = await API.get(`/v1/search/autocomplete?q=${encodeURIComponent(q)}&domain=${urlDomain}`);
      const data = res.data;
      const hasResults = (data.generators?.length || data.families?.length || data.sections?.length);
      setAutoResults(data);
      setShowAuto(!!hasResults || q.length >= 2);
    } catch { setShowAuto(false); }
  }

  useEffect(() => {
    function handler(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowAuto(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function submitSearch(q) {
    if (!q.trim()) return;
    setShowAuto(false);
    setSearchParams({ q: q.trim(), domain: urlDomain, page: 1, sort: urlSort });
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    submitSearch(searchInput);
  }

  function setDomain(d) {
    setSearchParams({ q: urlQ, domain: d, page: 1, sort: urlSort });
  }

  function setSort(s) {
    setSearchParams({ q: urlQ, domain: urlDomain, page: 1, sort: s });
  }

  function goPage(p) {
    setSearchParams({ q: urlQ, domain: urlDomain, page: p, sort: urlSort });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openGenerator(slug) {
    sessionStorage.setItem('active_slug', slug);
    navigate('/engine', { state: { slug } });
  }

  function openFamily(familySlug, sectionSlug) {
    navigate(`/generators?section=${encodeURIComponent(sectionSlug)}&family=${encodeURIComponent(familySlug)}`);
  }

  function openSection(sectionSlug) {
    navigate(`/generators?section=${encodeURIComponent(sectionSlug)}`);
  }

  function renderPagination() {
    if (!results || results.pages <= 1) return null;
    const { page, pages } = results;
    const btns  = [];
    const start = Math.max(1, page - 2);
    const end   = Math.min(pages, page + 2);
    if (page > 1) btns.push(<button key="prev" style={s.pageBtn} onClick={() => goPage(page - 1)}>prev</button>);
    if (start > 1) {
      btns.push(<button key={1} style={s.pageBtn} onClick={() => goPage(1)}>1</button>);
      if (start > 2) btns.push(<span key="d1" style={{ color: '#64748b' }}>...</span>);
    }
    for (let i = start; i <= end; i++) {
      btns.push(<button key={i} style={i === page ? s.pageBtnActive : s.pageBtn} onClick={() => goPage(i)}>{i}</button>);
    }
    if (end < pages) {
      if (end < pages - 1) btns.push(<span key="d2" style={{ color: '#64748b' }}>...</span>);
      btns.push(<button key={pages} style={s.pageBtn} onClick={() => goPage(pages)}>{pages}</button>);
    }
    if (page < pages) btns.push(<button key="next" style={s.pageBtn} onClick={() => goPage(page + 1)}>next</button>);
    return <div style={s.pagination}>{btns}</div>;
  }

  const hasResults = results && (results.generators.length > 0 || results.families.length > 0 || results.sections.length > 0);
  const hierarchy  = hasResults ? buildHierarchy(results) : [];

  return (
    <div style={s.page}>
      <SideHelpPanel />
      <ProductNav />

      <div style={s.container}>

        {/* SEARCH BAR */}
        <div style={s.searchWrap} ref={searchRef}>
          <form style={s.searchForm} onSubmit={handleSearchSubmit}>
            <input
              style={s.searchInput}
              placeholder="Search generators, families, or sections..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onFocus={() => { if (autoResults.generators?.length || autoResults.families?.length || searchInput.length >= 2) setShowAuto(true); }}
              autoComplete="off"
            />
            <button type="submit" style={s.searchBtn}>Search</button>
          </form>

          {showAuto && (
            <div style={s.autoList}>
              {autoResults.generators?.length > 0 && (
                <>
                  <div style={s.autoGroupLabel}>Generators</div>
                  {autoResults.generators.map((r, i) => (
                    <div key={`g${i}`} style={s.autoItem}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                      onClick={() => { setShowAuto(false); openGenerator(r.slug); }}>
                      <div style={s.autoName}>{r.displayName}</div>
                      <div style={s.autoSub}>{r.sectionDisplayName} → {r.familyDisplayName}</div>
                    </div>
                  ))}
                </>
              )}
              {autoResults.families?.length > 0 && (
                <>
                  <div style={s.autoGroupLabel}>Families</div>
                  {autoResults.families.map((r, i) => (
                    <div key={`f${i}`} style={s.autoItem}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                      onClick={() => { setShowAuto(false); navigate(`/generators?family=${r.family}`); }}>
                      <div style={s.autoName}>{r.familyDisplayName}</div>
                      <div style={s.autoSub}>{r.sectionDisplayName}</div>
                    </div>
                  ))}
                </>
              )}
              {autoResults.sections?.length > 0 && (
                <>
                  <div style={s.autoGroupLabel}>Sections</div>
                  {autoResults.sections.map((r, i) => (
                    <div key={`s${i}`} style={s.autoItem}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                      onClick={() => { setShowAuto(false); navigate(`/generators?section=${r.section}`); }}>
                      <div style={s.autoName}>{r.sectionDisplayName}</div>
                    </div>
                  ))}
                </>
              )}
              {searchInput.length >= 2 && (
                <div style={s.autoSearchAll}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                  onClick={() => submitSearch(searchInput)}>
                  Search for <strong style={{ color: '#a78bfa' }}>"{searchInput}"</strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CONTROLS */}
        <div style={s.controls}>
          <div style={s.controlGroup}>
            {[
              { key: 'dnd',        label: '🐉 D&D' },
              { key: 'profession', label: '💼 Professions' },
              { key: 'all',        label: '🌐 All Domains' },
            ].map(d => (
              <button key={d.key}
                style={urlDomain === d.key ? s.domainBtnActive : s.domainBtn}
                onClick={() => setDomain(d.key)}>
                {d.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {results && results.sections.length > 0 && (
              <div style={s.toggleGroup}>
                <button style={sectionView === 'cards' ? s.toggleActive : s.toggleBtn} onClick={() => setSectionView('cards')}>Cards</button>
                <button style={sectionView === 'compact' ? s.toggleActive : s.toggleBtn} onClick={() => setSectionView('compact')}>Compact</button>
              </div>
            )}
            <select style={s.sortSelect} value={urlSort} onChange={e => setSort(e.target.value)}>
              <option value="relevance">Relevance</option>
              <option value="alphabetical">Alphabetical</option>
              <option value="newest">Newest</option>
              <option value="popular">Popular</option>
            </select>
          </div>
        </div>

        {/* SUMMARY */}
        {results && !loading && (
          <div style={s.summary}>
            {results.totalResults.toLocaleString()} results in <strong>{results.domainDisplay}</strong>
            {results.pages > 1 && ` — page ${results.page} of ${results.pages}`}
          </div>
        )}

        {error && <div style={s.errorBox}>{error}</div>}
        {loading && <div style={s.spinner}>Searching...</div>}

        {!urlQ && !loading && (
          <div style={s.empty}>Enter a search term above to find generators, families, and sections.</div>
        )}

        {/* EMPTY RESULTS */}
        {results && !loading && !hasResults && (
          <div style={s.emptyBox}>
            <div style={s.emptyTitle}>No results for "{urlQ}"</div>
            {results.suggestions?.length > 0 && (
              <div style={s.suggestBox}>
                <div style={s.suggestLabel}>Did you mean</div>
                <div style={s.suggestRow}>
                  {results.suggestions.map(sg => (
                    <button key={sg.slug} style={s.suggestPill}
                            onClick={() => openGenerator(sg.slug)}>
                      {sg.displayName}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={s.emptyTips}>
              <div>Try broader terms</div>
              <div>Remove filters</div>
              <div>Switch to All Domains</div>
            </div>
          </div>
        )}

        {/* HIERARCHICAL RESULTS */}
        {!loading && hasResults && (
          <>
            {hierarchy.map((sec, si) => (
              <div key={`sec-${si}`} style={s.sectionBlock}>
                <div style={s.sectionHeader}
                  onClick={() => openSection(sec.section)}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={s.sectionName}>{sec.sectionDisplayName}</span>
                  {sec.generatorCount !== undefined && (
                    <span style={s.sectionCount}>{sec.generatorCount.toLocaleString()} generators</span>
                  )}
                  {urlDomain === 'all' && sec.domainDisplayName && (
                    <span style={s.domainBadge}>{sec.domainDisplayName}</span>
                  )}
                </div>

                {sec.families.length > 0 && (
                  sectionView === 'compact' ? (
                    <div style={s.compactFamList}>
                      {sec.families.map((fam, fi) => (
                        <div key={`fam-${fi}`} style={s.compactFamRow}
                          onClick={() => openFamily(fam.family, fam.section)}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.08)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.4)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}>
                          <span style={s.compactFamName}>{fam.familyDisplayName}</span>
                          {fam.generatorCount !== undefined && (
                            <span style={s.compactFamCount}>{fam.generatorCount} generators</span>
                          )}
                          {fam.generators.length > 0 && (
                            <span style={s.compactFamCount}>{fam.generators.length} results</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={s.famBlockGrid}>
                      {sec.families.map((fam, fi) => (
                        <div key={`fam-${fi}`} style={s.famBlock}>
                          <div style={s.famHeader}
                            onClick={() => openFamily(fam.family, fam.section)}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.1)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.5)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.04)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.2)'; }}>
                            <span style={s.famName}>{fam.familyDisplayName}</span>
                            {fam.generatorCount !== undefined && (
                              <span style={s.famCount}>{fam.generatorCount} generators</span>
                            )}
                          </div>

                          {sec.families.length > 1 && (
                            <div style={s.siblingRow}>
                              <span style={s.siblingLabel}>Also in {sec.sectionDisplayName}:</span>
                              {sec.families.filter(f => f.family !== fam.family).slice(0, 3).map((sf, sfi) => (
                                <span key={sfi} style={s.siblingChip}
                                  onClick={e => { e.stopPropagation(); openFamily(sf.family, sf.section); }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.25)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(124,58,237,0.1)'}>
                                  {sf.familyDisplayName}
                                </span>
                              ))}
                            </div>
                          )}

                          {fam.generators.length > 0 && (
                            <div style={s.genList}>
                              {fam.generators.map((gen, gi) => (
                                <div key={gi} style={s.genRow}
                                  onClick={() => openGenerator(gen.slug)}
                                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.4)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}>
                                  <div style={s.genRowLeft}>
                                    <span style={s.genRowName}>{gen.displayName}</span>
                                    <span style={s.genRowPath}>{gen.sectionDisplayName} → {gen.familyDisplayName}</span>
                                  </div>
                                  <div style={s.genRowRight}>
                                    {isRecentlyUpdated(gen.updatedAt) && <span style={s.recentBadge}>New</span>}
                                    {urlDomain === 'all' && <span style={s.domainBadge}>{gen.domainDisplayName}</span>}
                                    <span style={s.genArrow}>→</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                )}

                {sec.families.length === 0 && results.generators.filter(g => g.section === sec.section).length > 0 && (
                  <div style={s.genList}>
                    {results.generators.filter(g => g.section === sec.section).map((gen, gi) => (
                      <div key={gi} style={s.genRow}
                        onClick={() => openGenerator(gen.slug)}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.4)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}>
                        <div style={s.genRowLeft}>
                          <span style={s.genRowName}>{gen.displayName}</span>
                          <span style={s.genRowPath}>{gen.sectionDisplayName} → {gen.familyDisplayName}</span>
                        </div>
                        <div style={s.genRowRight}>
                          {isRecentlyUpdated(gen.updatedAt) && <span style={s.recentBadge}>New</span>}
                          <span style={s.genArrow}>→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {renderPagination()}
          </>
        )}

      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', system-ui, sans-serif", color: '#fff' },
  container: { maxWidth: 1200, margin: '0 auto', padding: '32px 40px' },
  searchWrap: { position: 'relative', maxWidth: 680, margin: '0 auto 24px' },
  searchForm: { display: 'flex', gap: 8 },
  searchInput: { flex: 1, padding: '13px 18px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 16, outline: 'none' },
  searchBtn: { padding: '13px 22px', ...glassSurface('#5B21B6', '#7c3aed', '#a855f7'), borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 15 },
  autoList: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#1e1b4b', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, marginTop: 4, overflow: 'hidden', zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' },
  autoGroupLabel: { padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.1em', borderTop: '1px solid rgba(255,255,255,0.06)' },
  autoItem: { padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.1s' },
  autoName: { color: '#fff', fontWeight: 500, fontSize: 13 },
  autoSub: { color: '#a78bfa', fontSize: 11, marginTop: 2 },
  autoSearchAll: { padding: '10px 16px', cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: 13, color: '#94a3b8', transition: 'background 0.1s' },
  controls: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  controlGroup: { display: 'flex', gap: 8 },
  domainBtn: { ...glassSurface('#334155', '#64748B', '#94A3B8'), padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s' },
  domainBtnActive: { ...glassSurface('#5B21B6', '#7c3aed', '#a855f7'), padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  toggleGroup: { display: 'flex', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, overflow: 'hidden' },
  toggleBtn: { background: 'rgba(255,255,255,0.05)', border: 'none', color: 'rgba(255,255,255,0.45)', padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  toggleActive: { background: 'rgba(124,58,237,0.3)', border: 'none', color: '#a78bfa', padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  sortSelect: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', outline: 'none' },
  summary: { color: '#94a3b8', fontSize: 14, marginBottom: 24 },
  spinner: { textAlign: 'center', color: '#a78bfa', padding: '60px 0', fontSize: 16 },
  empty: { textAlign: 'center', color: '#64748b', padding: '60px 0', fontSize: 16 },
  emptyBox: { textAlign: 'center', padding: '60px 0' },
  emptyTitle: { fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 16 },
  suggestBox: { marginBottom: 24 },
  suggestLabel: { color: '#94a3b8', fontSize: 14, marginBottom: 12 },
  suggestRow: { display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' },
  suggestPill: {
    borderRadius: 999, padding: '10px 20px', fontSize: 14, fontWeight: 700,
    color: '#fff', background: '#7c3aed', border: '2px solid #a78bfa',
    cursor: 'pointer', transition: 'transform .15s ease, border-color .15s ease',
  },
  emptyTips: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', color: '#64748b', fontSize: 14 },
  errorBox: { background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 8, padding: 16, color: '#fca5a5', marginBottom: 20 },
  sectionBlock: { marginBottom: 36 },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', cursor: 'pointer', marginBottom: 12, transition: 'background 0.15s' },
  sectionName: { fontSize: 17, fontWeight: 700, color: '#fff', flex: 1 },
  sectionCount: { fontSize: 12, color: '#64748b' },
  famBlockGrid: { display: 'flex', flexDirection: 'column', gap: 12, paddingLeft: 16 },
  famBlock: { borderRadius: 10, border: '1px solid rgba(124,58,237,0.15)', overflow: 'hidden' },
  famHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' },
  famName: { fontSize: 14, fontWeight: 600, color: '#c4b5fd', flex: 1 },
  famCount: { fontSize: 11, color: '#7c3aed', fontWeight: 600 },
  siblingRow: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', flexWrap: 'wrap', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  siblingLabel: { fontSize: 10, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  siblingChip: { fontSize: 11, color: '#a78bfa', background: 'rgba(124,58,237,0.1)', padding: '2px 10px', borderRadius: 10, cursor: 'pointer', transition: 'background 0.1s' },
  compactFamList: { display: 'flex', flexDirection: 'column', gap: 1, paddingLeft: 16 },
  compactFamRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 7, cursor: 'pointer', transition: 'all 0.1s', background: 'transparent' },
  compactFamName: { fontSize: 13, fontWeight: 600, color: '#c4b5fd', flex: 1 },
  compactFamCount: { fontSize: 11, color: '#475569' },
  genList: { display: 'flex', flexDirection: 'column', gap: 1, padding: '4px 0' },
  genRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 7, cursor: 'pointer', background: 'rgba(255,255,255,0.02)', transition: 'all 0.12s' },
  genRowLeft: { display: 'flex', flexDirection: 'column', gap: 2 },
  genRowName: { fontSize: 13, fontWeight: 600, color: '#fff' },
  genRowPath: { fontSize: 11, color: '#475569' },
  genRowRight: { display: 'flex', alignItems: 'center', gap: 6 },
  genArrow: { color: '#4c1d95', fontSize: 14, fontWeight: 700 },
  domainBadge: { background: 'rgba(139,92,246,0.15)', color: '#a78bfa', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 },
  recentBadge: { background: 'rgba(16,185,129,0.15)', color: '#10b981', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 32, flexWrap: 'wrap' },
  pageBtn: { padding: '8px 14px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: '#ccc', cursor: 'pointer', fontSize: 14 },
  pageBtnActive: { padding: '8px 14px', borderRadius: 7, background: 'linear-gradient(135deg, #7c3aed, #db2777)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700 },
};
